/**
 * SoleadLab 2.0 — Claim Entity API Client
 *
 * B3.4: Frontend service for claim entity CRUD.
 *
 * Read:  fetchClaims (auto-paginated list), fetchSingleClaim
 * Write: createClaim, updateClaim (If-Match ETag concurrency)
 *
 * V4 Invariant #5: "A Claim MUST reference an approved Finding"
 *   → POST will be rejected with 400 FINDING_NOT_APPROVED if the
 *     referenced finding is not yet approved.
 *   → Use isFindingNotApprovedError() to detect this in the UI
 *     and guide the user through the approved finding → claim flow.
 *
 * Status workflow (backend-enforced state machine):
 *   draft → approved | withdrawn
 *   approved → published | withdrawn
 *   published  (terminal)
 *   withdrawn  (terminal)
 *
 * Concurrency: PATCH requires If-Match: <version>.
 * Conflict: 409 VERSION_CONFLICT → isConflictError().
 * Bad transition: 422 INVALID_STATUS_TRANSITION → isStatusTransitionError().
 * Unapproved finding: 400 FINDING_NOT_APPROVED → isFindingNotApprovedError().
 *
 * Follows the same per-service pattern as findingApi.ts.
 */

import { apiFetch, apiFetchWithEtag } from './apiFetch';
export { isConflictError } from './apiFetch';

// ─── Types ──────────────────────────────────────────────────────────────────────

/** Raw row shape returned by the claim entity API */
export interface ClaimEntityRow {
  id: string;
  site_id: string;
  finding_id: string;
  claim_text: string;
  justification_json: string;            // JSON string: Record<string, unknown>
  category: string | null;
  status: string;
  approved_by: string | null;
  approved_at: string | null;
  version: number;
  created_at: string;
  updated_at: string;
}

/** Paginated list response */
interface ClaimListResponse {
  items: ClaimEntityRow[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

/** Frontend claim shape (parsed JSON fields) */
export interface Claim {
  id: string;
  siteId: string;
  findingId: string;
  claimText: string;
  justification: Record<string, unknown>;
  category: ClaimCategory | null;
  status: ClaimStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/** Status workflow values */
export type ClaimStatus = 'draft' | 'approved' | 'published' | 'withdrawn';

/** Category values */
export type ClaimCategory = 'warranty' | 'maintenance' | 'safety' | 'performance';

/** Filters for list endpoint */
export interface ClaimFilters {
  status?: ClaimStatus;
  category?: ClaimCategory;
  findingId?: string;
}

// ─── Fetch Utilities (thin adapters around centralized apiFetch) ──────────────

async function apiFetchGet<T>(path: string): Promise<T> {
  return apiFetch<T>(path);
}

async function apiFetchMutate<T>(
  path: string,
  method: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<{ data: T; etag: number }> {
  return apiFetchWithEtag<T>(path, {
    method,
    headers: extraHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

// ─── Transform ──────────────────────────────────────────────────────────────────

/** Parse justification_json string → Record safely */
function parseJustification(json: string): Record<string, unknown> {
  try {
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** Entity row → frontend Claim shape */
function entityRowToClaim(row: ClaimEntityRow): Claim {
  return {
    id: row.id,
    siteId: row.site_id,
    findingId: row.finding_id,
    claimText: row.claim_text,
    justification: parseJustification(row.justification_json),
    category: row.category as ClaimCategory | null,
    status: row.status as ClaimStatus,
    approvedBy: row.approved_by,
    approvedAt: row.approved_at,
    version: row.version,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ─── Error Helpers ──────────────────────────────────────────────────────────────

type ErrorWithMeta = Error & { status?: number; code?: string };

/** 422 INVALID_STATUS_TRANSITION */
export function isStatusTransitionError(err: unknown): boolean {
  return (
    err instanceof Error &&
    (err as ErrorWithMeta).status === 422 &&
    (err as ErrorWithMeta).code === 'INVALID_STATUS_TRANSITION'
  );
}

/**
 * 400 FINDING_NOT_APPROVED
 *
 * Indicates a claim creation attempt against a finding that is not yet
 * 'approved'. The caller should guide the user to first approve the finding
 * before creating a claim (approved finding → claim flow).
 */
export function isFindingNotApprovedError(err: unknown): boolean {
  return (
    err instanceof Error &&
    (err as ErrorWithMeta).status === 400 &&
    (err as ErrorWithMeta).code === 'FINDING_NOT_APPROVED'
  );
}

// ─── Read API ───────────────────────────────────────────────────────────────────

/**
 * Fetch ALL claims for a site. Auto-paginates (200/page).
 * Supports optional filters.
 */
export async function fetchClaims(
  siteId: string,
  filters?: ClaimFilters,
): Promise<{ items: Claim[]; total: number }> {
  const allItems: Claim[] = [];
  let page = 1;
  let total = 0;
  const limit = 200;

  while (true) {
    let url = `/api/v2/sites/${siteId}/claims?page=${page}&limit=${limit}`;
    if (filters?.status) url += `&status=${encodeURIComponent(filters.status)}`;
    if (filters?.category) url += `&category=${encodeURIComponent(filters.category)}`;
    if (filters?.findingId) url += `&finding_id=${encodeURIComponent(filters.findingId)}`;

    const result = await apiFetchGet<ClaimListResponse>(url);

    if (page === 1) total = result.total;

    for (const row of result.items) {
      allItems.push(entityRowToClaim(row));
    }

    if (!result.hasMore) break;
    page++;
  }

  return { items: allItems, total };
}

/**
 * Fetch a single claim by ID. Returns null on 404.
 */
export async function fetchSingleClaim(
  siteId: string,
  id: string,
): Promise<Claim | null> {
  try {
    const row = await apiFetchGet<ClaimEntityRow>(
      `/api/v2/sites/${siteId}/claims/${id}`,
    );
    return entityRowToClaim(row);
  } catch {
    return null;
  }
}

// ─── Write API ──────────────────────────────────────────────────────────────────

/**
 * Create a claim.
 *
 * V4 Invariant #5: The referenced finding MUST have status 'approved'.
 * If not, this throws a 400 FINDING_NOT_APPROVED error.
 * Use isFindingNotApprovedError() to catch this and prompt the user
 * to first approve the finding (the approved finding → claim flow).
 *
 * Returns the created claim with version from server.
 */
export async function createClaim(
  siteId: string,
  claim: {
    findingId: string;
    claimText: string;
    justification: Record<string, unknown>;
    category?: ClaimCategory;
    status?: ClaimStatus;
    id?: string;
  },
): Promise<Claim> {
  const body: Record<string, unknown> = {
    finding_id: claim.findingId,
    claim_text: claim.claimText,
    // justification_json must be sent as a JSON string (backend validates & stores as string)
    justification_json: JSON.stringify(claim.justification),
  };
  if (claim.id) body.id = claim.id;
  if (claim.category) body.category = claim.category;
  if (claim.status) body.status = claim.status;

  const { data: row, etag } = await apiFetchMutate<ClaimEntityRow>(
    `/api/v2/sites/${siteId}/claims`,
    'POST',
    body,
  );
  const result = entityRowToClaim(row);
  result.version = etag || result.version;
  return result;
}

/**
 * Update a claim. Requires version for optimistic concurrency (If-Match).
 *
 * Status transitions are backend-enforced:
 *   draft → approved | withdrawn
 *   approved → published | withdrawn
 *   published, withdrawn → terminal
 *
 * Throws 409 on version conflict → handle via isConflictError().
 * Throws 422 on bad transition → handle via isStatusTransitionError().
 */
export async function updateClaim(
  siteId: string,
  id: string,
  updates: {
    status?: ClaimStatus;
    claimText?: string;
    justification?: Record<string, unknown>;
    category?: ClaimCategory;
  },
  version: number,
): Promise<Claim> {
  const patchBody: Record<string, unknown> = {};
  if (updates.status !== undefined) patchBody.status = updates.status;
  if (updates.claimText !== undefined) patchBody.claim_text = updates.claimText;
  if (updates.justification !== undefined) {
    patchBody.justification_json = JSON.stringify(updates.justification);
  }
  if (updates.category !== undefined) patchBody.category = updates.category;

  const { data: row, etag } = await apiFetchMutate<ClaimEntityRow>(
    `/api/v2/sites/${siteId}/claims/${id}`,
    'PATCH',
    patchBody,
    { 'If-Match': String(version) },
  );
  const result = entityRowToClaim(row);
  result.version = etag || result.version;
  return result;
}
